/**
 * RegionScopeStrip — professional strip at the top of dashboards that
 * shows the caller's assigned region + live counts (properties, staff,
 * cities). Renders inline once when the user is region-scoped
 * (Supervisor / Crew Leader / Agent). Silent for global admins.
 *
 * Fetched from `GET /api/regions/my-scope` so a single call powers
 * every downstream read (map, chat, schedules, employees) that filters
 * by the same region.
 */
import { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { MapPin, Users, Building2, Compass, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// NECS 12 commercial service lines (short labels), rendered as a 4×3 grid
// on the right of the region strip in place of the old NECS HQ address.
const BUSINESS_TYPES = [
  { key: 'office', name: 'Offices' },
  { key: 'retail', name: 'Retail' },
  { key: 'gas_station', name: 'Gas Stations' },
  { key: 'school', name: 'Schools' },
  { key: 'medical', name: 'Medical' },
  { key: 'warehouse', name: 'Warehouses' },
  { key: 'restaurant', name: 'Restaurants' },
  { key: 'mall', name: 'Malls' },
  { key: 'church', name: 'Churches' },
  { key: 'bank', name: 'Banking' },
  { key: 'stadium', name: 'Stadiums' },
  { key: 'airport', name: 'Airports' },
];

export default function RegionScopeStrip({ compact = false, className = '' }) {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/routes/regions/my-scope`, { credentials: 'include' });
        if (res.ok && alive) setScope(await res.json());
      } catch (_) { /* noop */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !scope || !scope.scoped) return null;

  // Region-scoped role without an assigned region — surface the warning
  if (!scope.region) {
    return (
      <div
        className={`bg-amber-950/40 border-2 border-amber-500/60 rounded-lg p-3 flex items-start gap-3 ${className}`}
        data-testid="region-scope-strip-empty"
      >
        <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-100">Region assignment pending</p>
          <p className="text-xs text-amber-200/80 mt-0.5">
            Ask NECS admin to assign you a region so your dashboard populates with the correct
            properties, staff, and crew map.
          </p>
        </div>
      </div>
    );
  }

  const r = scope.region;
  const c = scope.counts || {};

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`} data-testid="region-scope-strip-compact">
        <Badge className="bg-[#FFD60A] text-black uppercase">{r.region_code || 'REG'}</Badge>
        <span className="text-sm font-bold text-white">{r.region_name}</span>
      </div>
    );
  }

  return (
    <div
      className={`bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-800 border-2 border-[#FFD60A]/40 rounded-lg p-4 flex items-center gap-4 flex-wrap ${className}`}
      data-testid="region-scope-strip"
    >
      <div className="flex items-center gap-3">
        <span className="p-2 bg-[#FFD60A] text-black rounded-lg">
          <MapPin className="h-5 w-5" />
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">You are managing</p>
          <p className="text-lg font-bold text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {r.region_name?.toUpperCase()}
            {r.region_code && (
              <span className="ml-2 text-xs text-[#FFD60A] font-mono">{r.region_code}</span>
            )}
          </p>
        </div>
      </div>

      <div className="h-10 w-px bg-zinc-700 hidden md:block" />

      <Stat icon={<MapPin className="h-4 w-4" />} label="Cities" value={r.cities?.length ?? 0} tone="text-[#FFD60A]" />
      <Stat icon={<Building2 className="h-4 w-4" />} label={scope.service === 'valet' ? 'Communities' : 'Properties'} value={c.properties ?? 0} tone="text-emerald-400" />
      <Stat icon={<Users className="h-4 w-4" />} label="Staff on your team" value={c.staff ?? 0} tone="text-sky-400" />

      {/* Right side — Cleaners: NECS 12 commercial service lines (4 × 3).
          Valet: the single Multifamily service line. */}
      {scope.service === 'valet' ? (
        <div className="ml-auto w-full md:w-auto" data-testid="region-scope-valet-type">
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1.5">Service line</p>
          <span className="inline-block text-xs leading-tight text-black font-bold bg-[#FFD60A] rounded px-3 py-1.5" data-testid="region-scope-type-multifamily">
            Multifamily · Residential · Apartments &amp; Condos
          </span>
        </div>
      ) : (
      <div className="ml-auto w-full md:w-auto" data-testid="region-scope-business-types">
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1.5">Commercial service lines</p>
        <div className="grid grid-cols-4 gap-1.5">
          {BUSINESS_TYPES.map((t) => (
            <span
              key={t.key}
              title={t.name}
              data-testid={`region-scope-type-${t.key}`}
              className="text-[10px] leading-tight text-zinc-200 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-center truncate"
            >
              {t.name}
            </span>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, tone }) {
  // If the value is long text (like an address), shrink to text-sm so it
  // fits the strip row without wrapping into the next tile.
  const isText = typeof value === 'string' && value.length > 8;
  return (
    <div className="flex items-center gap-2">
      <span className={tone}>{icon}</span>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{label}</p>
        <p className={`${isText ? 'text-xs' : 'text-xl'} font-bold leading-tight ${tone}`}>{value}</p>
      </div>
    </div>
  );
}
